/**
 * Environment Configuration - UI
 *
 * Centralized environment variable access for the UI.
 * All environment variables should be accessed through this module.
 */

// Server URLs
export const AUTH_SERVER_URL =
  import.meta.env.VITE_AUTH_SERVER_URL || "http://localhost:3001";
export const ORPC_SERVER_URL =
  import.meta.env.VITE_ORPC_SERVER_URL || "http://localhost:3002";
export const REST_API_URL =
  import.meta.env.VITE_REST_API_URL || "http://localhost:3000/api";

// Authentication Configuration
export const AUTH_REQUIRED = import.meta.env.VITE_AUTH_REQUIRED !== "false";

// Casdoor Configuration
export const CASDOOR_SERVER_URL =
  import.meta.env.VITE_CASDOOR_SERVER_URL || "http://localhost:8000";
export const CASDOOR_CLIENT_ID = import.meta.env.VITE_CASDOOR_CLIENT_ID || "";
export const CASDOOR_ORGANIZATION =
  import.meta.env.VITE_CASDOOR_ORGANIZATION || "built-in";
export const CASDOOR_APP_NAME = import.meta.env.VITE_CASDOOR_APP_NAME || "ive";

// Environment
export const NODE_ENV =
  import.meta.env.VITE_NODE_ENV || import.meta.env.MODE || "development";
export const IS_DEV = NODE_ENV === "development";
export const IS_PROD = NODE_ENV === "production";

// Workspace Configuration
export const DEFAULT_WORKSPACE_ID =
  import.meta.env.VITE_DEFAULT_WORKSPACE_ID ||
  "00000000-0000-4000-8000-000000000000";
export const DEFAULT_CONVERSATION_ID =
  import.meta.env.VITE_DEFAULT_CONVERSATION_ID ||
  "00000000-4000-8000-0001-000000000000";
export const DEFAULT_THREAD_ID =
  import.meta.env.VITE_DEFAULT_THREAD_ID ||
  "00000000-4000-8000-0002-000000000000";
export const MAX_WORKSPACES_PER_USER = parseInt(
  import.meta.env.VITE_IVE_MAX_WORKSPACES_PER_USER || "10",
);
export const WORKSPACE_NAME_MAX_LENGTH = parseInt(
  import.meta.env.VITE_IVE_WORKSPACE_NAME_MAX_LENGTH || "100",
);
export const AUTO_CREATE_DEFAULT_WORKSPACE =
  import.meta.env.VITE_IVE_AUTO_CREATE_DEFAULT_WORKSPACE !== "false";

// Integration Configuration
export const INTEGRATIONS_ENABLED =
  import.meta.env.VITE_IVE_INTEGRATIONS_ENABLED !== "false";
export const NATS_ENABLED = import.meta.env.VITE_IVE_NATS_ENABLED === "true";
export const NATS_SERVERS = import.meta.env.VITE_IVE_NATS_SERVERS?.split(
  ",",
) || ["ws://localhost:8082"];
export const ORPC_ENABLED = import.meta.env.VITE_ORPC_ENABLED === "true";

// Protocol Configuration
export const PROTOCOL_TYPE = import.meta.env.VITE_PROTOCOL_TYPE || "orpc"; // "orpc" | "rest"

// Connection & Retry Configuration
export const ORPC_RETRY_INTERVAL = parseInt(
  import.meta.env.VITE_ORPC_RETRY_INTERVAL || "5000", // Changed from 10000
);
export const ORPC_CONNECTION_TIMEOUT = parseInt(
  import.meta.env.VITE_ORPC_CONNECTION_TIMEOUT || "15000", // Changed from 30000
);
export const ORPC_MAX_RETRY_ATTEMPTS = parseInt(
  import.meta.env.VITE_ORPC_MAX_RETRY_ATTEMPTS || "2", // Changed from 0
);
export const ORPC_HEALTH_CHECK_INTERVAL = parseInt(
  import.meta.env.VITE_ORPC_HEALTH_CHECK_INTERVAL || "30000", // Changed from 60000
);

// Pane Configuration
export const PANE_TAB_BAR_VISIBLE =
  import.meta.env.VITE_IVE_PANE_TAB_BAR_VISIBLE !== "false";
export const PANE_TAB_BAR_TOGGLEABLE =
  import.meta.env.VITE_IVE_PANE_TAB_BAR_TOGGLEABLE !== "false";

export const PANE_ASSETS_VISIBLE =
  import.meta.env.VITE_IVE_PANE_ASSETS_VISIBLE !== "false";
export const PANE_ASSETS_WIDTH = parseInt(
  import.meta.env.VITE_IVE_PANE_ASSETS_WIDTH || "250",
);
export const PANE_ASSETS_TOGGLEABLE =
  import.meta.env.VITE_IVE_PANE_ASSETS_TOGGLEABLE !== "false";

export const PANE_TOOLS_VISIBLE =
  import.meta.env.VITE_IVE_PANE_TOOLS_VISIBLE !== "false";
export const PANE_TOOLS_WIDTH = parseInt(
  import.meta.env.VITE_IVE_PANE_TOOLS_WIDTH || "250",
);
export const PANE_TOOLS_TOGGLEABLE =
  import.meta.env.VITE_IVE_PANE_TOOLS_TOGGLEABLE !== "false";

export const PANE_ASSISTANT_VISIBLE =
  import.meta.env.VITE_IVE_PANE_ASSISTANT_VISIBLE !== "false";
export const PANE_ASSISTANT_WIDTH = parseInt(
  import.meta.env.VITE_IVE_PANE_ASSISTANT_WIDTH || "600",
);
export const PANE_ASSISTANT_HEIGHT = parseInt(
  import.meta.env.VITE_IVE_PANE_ASSISTANT_HEIGHT || "200",
);
export const PANE_ASSISTANT_TOGGLEABLE =
  import.meta.env.VITE_IVE_PANE_ASSISTANT_TOGGLEABLE !== "false";
export const PANE_ASSISTANT_FULL_SCREEN =
  import.meta.env.VITE_IVE_PANE_ASSISTANT_FULL_SCREEN === "true";

// Environment configuration object
const envConfig = {
  servers: {
    auth: AUTH_SERVER_URL,
    orpc: ORPC_SERVER_URL,
    rest: REST_API_URL,
    casdoor: CASDOOR_SERVER_URL,
  },
  protocol: {
    type: PROTOCOL_TYPE,
    orpcEnabled: ORPC_ENABLED,
  },
  auth: {
    required: AUTH_REQUIRED,
  },
  casdoor: {
    clientId: CASDOOR_CLIENT_ID,
    organization: CASDOOR_ORGANIZATION,
    appName: CASDOOR_APP_NAME,
  },
  workspace: {
    defaultWorkspaceId: DEFAULT_WORKSPACE_ID,
    maxWorkspacesPerUser: MAX_WORKSPACES_PER_USER,
    defaultConversationId: DEFAULT_CONVERSATION_ID,
    defaultThreadId: DEFAULT_THREAD_ID,
    workspaceNameMaxLength: WORKSPACE_NAME_MAX_LENGTH,
    autoCreateDefaultWorkspace: AUTO_CREATE_DEFAULT_WORKSPACE,
  },
  environment: {
    nodeEnv: NODE_ENV,
    isDev: IS_DEV,
    isProd: IS_PROD,
  },
  integrations: {
    enabled: INTEGRATIONS_ENABLED,
    nats: {
      enabled: NATS_ENABLED,
      servers: NATS_SERVERS,
    },
    orpc: {
      enabled: ORPC_ENABLED,
      retryInterval: ORPC_RETRY_INTERVAL,
      connectionTimeout: ORPC_CONNECTION_TIMEOUT,
      maxRetryAttempts: ORPC_MAX_RETRY_ATTEMPTS,
      healthCheckInterval: ORPC_HEALTH_CHECK_INTERVAL,
    },
  },
  panes: {
    tabBar: {
      visible: PANE_TAB_BAR_VISIBLE,
      toggleable: PANE_TAB_BAR_TOGGLEABLE,
    },
    assets: {
      visible: PANE_ASSETS_VISIBLE,
      width: PANE_ASSETS_WIDTH,
      toggleable: PANE_ASSETS_TOGGLEABLE,
    },
    tools: {
      visible: PANE_TOOLS_VISIBLE,
      width: PANE_TOOLS_WIDTH,
      toggleable: PANE_TOOLS_TOGGLEABLE,
    },
    assistant: {
      visible: PANE_ASSISTANT_VISIBLE,
      width: PANE_ASSISTANT_WIDTH,
      height: PANE_ASSISTANT_HEIGHT,
      toggleable: PANE_ASSISTANT_TOGGLEABLE,
      fullScreen: PANE_ASSISTANT_FULL_SCREEN,
    },
  },
  // Chat Configuration
  maxChatQueue: Number(import.meta.env.VITE_IVE_MAX_CHAT_QUEUE) || 100,

  // Focus Configuration
  focusComposer: import.meta.env.VITE_FOCUS_COMPOSER === "true",
} as const;

export type EnvConfig = typeof envConfig;

// Export the config object for use as parameter
export { envConfig };

// Log configuration in development
if (IS_DEV) {
  console.log("\n\n\n🔧 Environment configuration:", envConfig);
}

// Extension-specific window interface
interface ExtensionWindow extends Window {
  __IVE_EXTENSION_MODE__?: boolean;
  __IVE_ENV__?: {
    MODE?: string;
    COMPACT?: boolean;
  };
}

// Extension mode detection
export const isExtensionMode = () => {
  return (
    typeof window !== "undefined" &&
    (window as ExtensionWindow).__IVE_EXTENSION_MODE__ === true
  );
};

// Extension-specific environment
export const extensionEnv = {
  isExtension: isExtensionMode(),
  isCompact:
    typeof window !== "undefined" &&
    (window as ExtensionWindow).__IVE_ENV__?.COMPACT === true,
  mode:
    (typeof window !== "undefined" &&
      (window as ExtensionWindow).__IVE_ENV__?.MODE) ||
    "web",
};

// Extension configuration for UI adaptation
export const getExtensionConfig = () => {
  if (!extensionEnv.isExtension) {
    return null;
  }

  return {
    // Extension-specific UI adaptations
    compact: true,
    showHeader: false,
    showSidebar: false,
    maxHeight: "600px",
    maxWidth: "400px",

    // Extension-specific features
    enableFullscreenButton: true,
    enableOptionsButton: true,

    // Extension-specific styling
    containerClass: "extension-mode",
    spacing: "compact",
  };
};
